import { API_BASE } from './config';
import type { Manifest, FetchManifestResult } from './types';

export class ApiError extends Error {
  constructor(public status: number, message: string) {
    super(message);
    this.name = 'ApiError';
  }
}

export class UnauthorizedError extends ApiError {
  constructor() {
    super(401, 'Unauthorized');
    this.name = 'UnauthorizedError';
  }
}

export class NetworkError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'NetworkError';
  }
}

export interface PairResponse {
  deviceToken: string;
  screen: {
    id: string;
    name: string;
    orientation: string;
    resolutionW: number;
    resolutionH: number;
  };
  manifest: Manifest;
}

export async function pair(
  activationCode: string,
  deviceFingerprint: string,
): Promise<PairResponse> {
  const res = await safeFetch(`${API_BASE}/player/pair`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ activationCode, deviceFingerprint }),
  });
  if (!res.ok) {
    let detail = '';
    try {
      const body = await res.json();
      detail = body?.message ?? '';
    } catch {
      // ignore parse failures
    }
    throw new ApiError(res.status, detail || `Pair failed (${res.status})`);
  }
  return (await res.json()) as PairResponse;
}

export async function fetchManifest(
  deviceToken: string,
  ifNoneMatch: string | null,
): Promise<FetchManifestResult> {
  const headers: Record<string, string> = {
    Authorization: `Device ${deviceToken}`,
  };
  if (ifNoneMatch) headers['If-None-Match'] = ifNoneMatch;

  const res = await safeFetch(`${API_BASE}/player/manifest`, { headers });
  if (res.status === 304) return { kind: 'NOT_MODIFIED' };
  if (res.status === 401) throw new UnauthorizedError();
  if (!res.ok) throw new ApiError(res.status, `Manifest fetch failed (${res.status})`);

  const manifest = (await res.json()) as Manifest;
  const etag = res.headers.get('etag') ?? manifest.manifest_hash;
  return { kind: 'OK', manifest, etag };
}

async function safeFetch(input: RequestInfo, init?: RequestInit): Promise<Response> {
  try {
    return await fetch(input, init);
  } catch (err) {
    throw new NetworkError(err instanceof Error ? err.message : String(err));
  }
}
